import { FC } from 'react';
import { ChevronLeft, ChevronRight, Users, Search, Loader2, MoreVertical } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import api from '@/utils/api';
import { useEditMode } from '@/hooks/useEditMode';
import { useUser } from '@/hooks';
import { useGlobalLoading } from '@/contexts/GlobalLoadingContext';
import { useSelectedUser } from '@/contexts/SelectedUserContext';
import { getErrorMessage } from '@/utils/error';


type UserStatus = "ACTIVE" | "INACTIVE" | "DEMO";

interface ExternalUsersSidebarProps {
  className?: string;
  onEditModeChange?: (isEditMode: boolean) => void;
  onUserSelect?: (user: ExternalUser | null) => void;
  allowDeselect?: boolean;
}

interface ExternalUser {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  company?: string;
  phone?: string;
  city?: string;
  country?: string;
  lastLogin?: string;
  status?: string;
  role?: string;
}

interface UsersApiResponse {
  items: ExternalUser[];
  total: number;
  page: number;
  limit: number;
}

export const ExternalUsersSidebar: FC<ExternalUsersSidebarProps> = ({
  className = '',
  onEditModeChange,
  onUserSelect,
  allowDeselect = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<ExternalUser[]>([]);
  const { selectedUser, setSelectedUser } = useSelectedUser();
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [statusModal, setStatusModal] = useState<{
    open: boolean;
    user: ExternalUser | null;
  }>({ open: false, user: null });

  const [statusValue, setStatusValue] = useState<UserStatus>("DEMO");
  const [savingStatus, setSavingStatus] = useState(false);

  const { showLoading, hideLoading } = useGlobalLoading();
  const { isEditMode, toggleEditMode, setUserRole, addToHistory } = useEditMode();
  const user = useUser();

  useEffect(() => {
    if (user?.role) {
      setUserRole(user.role as 'internal' | 'external');
    }
  }, [user, setUserRole]);

  const fetchUsers = useCallback(async (query?: string) => {
    setLoading(true);
    setFetchError(null);
    try {
      const params = { page: 1, limit: 100, ...(query && { q: query }) };
      const response = await api.get<UsersApiResponse>('/users/external', { params });
      setUsers(response.data.items || []);
    } catch (err: unknown) {
      setFetchError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchUsers(searchQuery), 200);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchUsers]);

  useEffect(() => {
    if (users.length > 0 && !selectedUser) {
      setSelectedUser(users[0]);
    }
  }, [users, selectedUser, setSelectedUser]);

  const getUserInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const getAvatarColor = (name: string) => {
    const colors = ['bg-blue-600', 'bg-indigo-600', 'bg-purple-600', 'bg-pink-600', 'bg-rose-600', 'bg-emerald-600', 'bg-teal-600'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const getAvatarUrl = (avatar?: string) => {
    if (!avatar) return undefined;
    if (avatar.startsWith('http')) return avatar;
    const baseURL = api.defaults.baseURL || '';
    return avatar.startsWith('/') ? `${baseURL}${avatar}` : `${baseURL}/${avatar}`;
  };

  const getUserStatus = (lastLogin?: string) => {
    if (!lastLogin) return 'offline';
    const hours = (new Date().getTime() - new Date(lastLogin).getTime()) / (1000 * 60 * 60);
    return hours < 1 ? 'online' : hours < 24 ? 'away' : 'offline';
  };

  const getStatusColor = (s: string) => s === 'online' ? 'bg-emerald-500' : s === 'away' ? 'bg-amber-500' : 'bg-gray-400';

  const getUserStatusLabel = (s?: string) => {
    const v = (s || "demo").toLowerCase();
    return v === "active" ? "Active" : v === "inactive" ? "Inactive" : "Demo";
  };

  const getUserStatusBadgeClass = (s?: string) => {
    const v = (s || "demo").toLowerCase();
    if (v === "active") return "bg-emerald-500/15 text-emerald-300 border-emerald-500/20";
    if (v === "inactive") return "bg-gray-500/15 text-gray-300 border-gray-500/20";
    return "bg-amber-500/15 text-amber-300 border-amber-500/20";
  };

  const toApiStatus = (ui: UserStatus) => ui.toLowerCase();
  const toUiStatus = (apiS?: string) => (apiS?.toUpperCase() || "DEMO") as UserStatus;

  const openStatusModal = (u: ExternalUser) => {
    setStatusValue(toUiStatus(u.status));
    setStatusModal({ open: true, user: u });
  };

  const closeStatusModal = () => setStatusModal({ open: false, user: null });

  const updateExternalUserStatus = async (userId: string, status: UserStatus) => {
    const res = await api.patch(`/users/${userId}/status`, { status: toApiStatus(status) });
    return res.data;
  };

  const toggleExpanded = () => setIsExpanded(!isExpanded);
  const handleToggleEditMode = () => {
    if (user?.role !== 'internal' && user?.role !== 'admin') return;
    toggleEditMode();
    onEditModeChange?.(!isEditMode);
  };

  return (
    <>
      <aside className={`h-full bg-slate-900 border-r border-slate-800 flex-shrink-0 transition-all duration-200 ${isExpanded ? 'w-64' : 'w-16'} ${className}`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex flex-col gap-3 p-4 border-b border-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                {isExpanded && <h2 className="text-sm font-semibold text-slate-100">External Users</h2>}
              </div>
              <button onClick={toggleExpanded} className="p-1.5 text-slate-500 hover:text-slate-100 hover:bg-slate-800 rounded-lg">
                {isExpanded ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            </div>

            {isExpanded && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            )}

            {(user?.role === 'internal' || user?.role === 'admin') && (
              <div className="pt-2">
                {isExpanded ? (
                  <div className="bg-slate-800 rounded-lg p-1 border border-slate-700 flex">
                    <button onClick={handleToggleEditMode} className={`flex-1 py-1 text-xs rounded transition-colors ${!isEditMode ? 'bg-slate-700 text-white' : 'text-slate-400'}`}>View</button>
                    <button onClick={handleToggleEditMode} className={`flex-1 py-1 text-xs rounded transition-colors ${isEditMode ? 'bg-slate-700 text-white' : 'text-slate-400'}`}>Edit</button>
                  </div>
                ) : (
                  <button onClick={handleToggleEditMode} className={`w-full py-2 rounded border ${isEditMode ? 'bg-blue-500/20 border-blue-500/30 text-blue-400' : 'bg-slate-800 border-slate-700 text-slate-500'}`}>
                    <MoreVertical className="w-4 h-4 mx-auto" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto dark-scrollbar p-2 space-y-1">
            {loading ? (
              <div className="p-4 text-center text-slate-500 text-xs">Loading...</div>
            ) : fetchError ? (
              <div className="p-4 text-center">
                <div className="text-red-400 text-xs mb-2">{fetchError}</div>
                <button onClick={() => fetchUsers(searchQuery)} className="text-slate-300 text-xs hover:text-white">Retry</button>
              </div>
            ) : users.length === 0 ? (
              <div className="p-6 text-center text-slate-600 text-sm">No users found</div>
            ) : (
              users.map(u => {
                const uStatus = getUserStatus(u.lastLogin);
                const isSelected = selectedUser?.id === u.id;
                return (
                  <div
                    key={u.id}
                    onClick={() => {
                      if (!isSelected) {
                        setSelectedUser(u);
                        showLoading(`Loading ${u.name}...`);
                        if (onUserSelect) onUserSelect(u);

                        // Log selection to history
                        addToHistory({
                          action: 'user_selection',
                          tableName: 'external_users',
                          recordId: u.id,
                          changes: { name: u.name, selected: true }
                        });

                        setTimeout(hideLoading, 500);
                      } else if (allowDeselect) {
                        setSelectedUser(null);
                        if (onUserSelect) onUserSelect(null);
                      }
                    }}
                    className={`p-2 rounded-lg cursor-pointer transition-all border ${isSelected ? 'bg-blue-500/10 border-blue-500/30' : 'hover:bg-slate-800 border-transparent'}`}
                  >
                    {isExpanded ? (
                      <div className="flex items-center gap-3">
                        <div className="relative shrink-0">
                          {getAvatarUrl(u.avatar) ? (
                            <img src={getAvatarUrl(u.avatar)} alt={u.name} className="w-8 h-8 rounded-full border border-slate-700" />
                          ) : (
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium ${getAvatarColor(u.name)}`}>
                              {getUserInitials(u.name)}
                            </div>
                          )}
                          <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-slate-900 ${getStatusColor(uStatus)}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-medium text-slate-100 truncate">{u.name}</h3>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border leading-none ${getUserStatusBadgeClass(u.status)}`}>{getUserStatusLabel(u.status)}</span>
                          </div>
                          <p className="text-xs text-slate-500 truncate">{u.email}</p>
                        </div>
                        {(user?.role === 'internal' || user?.role === 'admin') && (
                          <button onClick={(e) => { e.stopPropagation(); openStatusModal(u); }} className="p-1 text-slate-500 hover:text-white">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="relative flex justify-center group">
                        <div className="relative">
                          {getAvatarUrl(u.avatar) ? (
                            <img src={getAvatarUrl(u.avatar)} alt={u.name} className="w-8 h-8 rounded-full border border-slate-700" />
                          ) : (
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium ${getAvatarColor(u.name)}`}>
                              {getUserInitials(u.name)}
                            </div>
                          )}
                          <div className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-slate-900 ${getStatusColor(uStatus)}`} />
                        </div>
                        {(user?.role === 'internal' || user?.role === 'admin') && (
                          <button onClick={(e) => { e.stopPropagation(); openStatusModal(u); }} className="absolute -top-1 -left-1 opacity-0 group-hover:opacity-100 p-1 bg-slate-800 border border-slate-700 rounded-full text-slate-400">
                            <MoreVertical className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </aside>

      {statusModal.open && statusModal.user && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4" onClick={closeStatusModal}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-100 mb-4">Update Status</h3>
            <p className="text-sm text-slate-400 mb-6">User: <span className="text-slate-200">{statusModal.user.name}</span></p>

            <div className="space-y-4">
              <select
                value={statusValue}
                onChange={(e) => setStatusValue(e.target.value as UserStatus)}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="DEMO">DEMO</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button onClick={closeStatusModal} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancel</button>
              <button
                disabled={savingStatus}
                onClick={async () => {
                  if (!statusModal.user) return;
                  setSavingStatus(true);
                  try {
                    const res = await updateExternalUserStatus(statusModal.user.id, statusValue);
                    setUsers(prev => prev.map(u => u.id === res.id ? { ...u, status: res.status } : u));
                    if (selectedUser && selectedUser.id === res.id) {
                      setSelectedUser({ ...selectedUser, status: res.status });
                    }

                    // Log status change to history
                    addToHistory({
                      action: 'update_status',
                      tableName: 'external_users',
                      recordId: statusModal.user.id,
                      changes: { status: res.status }
                    });

                    closeStatusModal();
                  } catch (err) {
                    console.error(err);
                  } finally {
                    setSavingStatus(false);
                  }
                }}
                className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {savingStatus && <Loader2 className="w-4 h-4 animate-spin" />}
                {savingStatus ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};